<template>
  <a-segmented
    v-model:value="innerValue"
    :options="normalizedOptions"
    :disabled="disabled"
    :size="size"
    :block="block"
    class="ant-segmented"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { computed, h } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import type { SegmentedOption } from "./types";

interface Props {
  modelValue?: string | number;
  options?: Array<string | number | SegmentedOption>;
  disabled?: boolean;
  size?: SizeType;
  block?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  options: () => [],
  disabled: false,
  size: "middle",
  block: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number): void;
  (e: "change", value: string | number): void;
}>();

const normalizedOptions = computed(() =>
  (props.options || []).map((opt) => {
    if (typeof opt === "string" || typeof opt === "number") {
      return { label: String(opt), value: opt };
    }
    if (opt.icon) {
      return {
        value: opt.value,
        disabled: opt.disabled,
        payload: opt,
        label: () =>
          h("span", { class: "ant-segmented-label" }, [
            h("i", { class: opt.icon }),
            opt.label ? h("span", opt.label) : null,
          ]),
      };
    }
    return {
      label: opt.label,
      value: opt.value,
      disabled: opt.disabled,
    };
  })
);

const innerValue = computed({
  get: () => props.modelValue as string | number,
  set: (val: string | number) => {
    emit("update:modelValue", val);
    emit("change", val);
  },
});

const onChange = (val: string | number) => {
  emit("update:modelValue", val);
  emit("change", val);
};
</script>

<style scoped>
.ant-segmented {
  max-width: 100%;
}

.ant-segmented :deep(.ant-segmented-label) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>

<!--
  Hướng dẫn sử dụng — Segmented Control
  Global: <h-segmented v-model="view" :options="['List', 'Grid']" />
  Import: import { Segmented } from '@/components/ant/select'

  Props: modelValue, options (string[] | { label, value, icon? }[]), disabled, size, block
  Events: update:modelValue, change
  Docs: src/components/ant/select/README.md
-->
